import { createContext, useContext, useEffect, useState } from "react";
import { useEventAction } from "./EventContext";
import { useView } from "./ViewContext";

const NextStepContext = createContext();

export const useNextStep = () => useContext(NextStepContext);

/**
 * Resolves what the backend's `next-step` control means and carries it out.
 */
export const NextStepProvider = ({ children }) => {
    const { view, setView, advance, readView } = useView();
    const [pending, setPending] = useState(null);
    const [ending, setEnding] = useState(null);
    const [visible, setVisible] = useState(false);
    const [canProceed, setCanProceed] = useState(false);

    useEffect(() => {
        if (view) setVisible(view.way_forward === "continue");
    }, [view]);

    const resolveEnding = async () => {
        setEnding(null);

        let control;
        try {
            const settled = await readView();
            control = settled.controls.find(c => c.action === "next-step");
        } catch (err) {
            // Back to the journeys rather than stranded behind a spent dialog; the
            // button is waiting on the roadmap when they return.
            console.error("Failed to resolve the ending:", err);
        }

        if (control?.predicate === "next-view") {
            await advance();
        } else {
            setView("close");
        }
    };

    const activate = async () => {
        setCanProceed(false);
        setPending(null);

        if (pending === "beta-invite") {
            setVisible(false);
            setEnding("beta-invite");
            return;
        }

        if (pending === "close") {
            setVisible(false);
            setView("close");
            return;
        }

        await advance();
    };

    useEventAction("next-step", (event) => {
        switch (event.predicate) {
            // Not routed through `activate`: this fires from inside the event handler,
            // where `pending` is still the value this very event is about to set.
            case "auto-progress":
                setCanProceed(false);
                advance();
                break;

            case "next-view":
            case "beta-invite":
            case "close":
                setPending(event.predicate);
                setVisible(true);
                setCanProceed(true);
                break;

            case null:
            default:
                console.warn("Next-step event missing predicate " + event);
                console.info("Ignoring event.");
                setPending(null);
                setVisible(true);
                setCanProceed(true);
                break;
        }
    });

    return (
        <NextStepContext.Provider value={{ visible, canProceed, activate, ending, resolveEnding }}>
            {children}
        </NextStepContext.Provider>
    );
};
